/**
 * The signed-in account's usage this billing period → `GET /pwa/api/usage`.
 * Content-free: token/call counts, caps, and what's left.
 */
import { api } from './api';

export interface Usage {
  plan: string;
  period: { start: string; end: string };
  usage: {
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
    calls: number;
    cost_usd: number;
  };
  limits: {
    monthly_token_cap: number | null;
    monthly_call_cap: number | null;
    rate_limit_per_min: number;
  };
  remaining: { tokens: number | null; calls: number | null };
}

export interface DailyPoint {
  day: string;
  total_tokens: number;
  calls: number;
  cost_usd: number;
}

export interface RecentCall {
  timestamp: string;
  phase: string | null;
  workload: string | null;
  upstream_provider: string | null;
  upstream_model: string | null;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cost_usd: number;
  provider_ms: number | null;
  client_ms: number | null;
  ok: boolean;
  error_code: string | null;
}

export interface ModelRow {
  model: string;
  calls: number;
  total_tokens: number;
  cost_usd: number;
}

const num = (v: unknown): number => (v == null ? 0 : Number(v));
const numOrNull = (v: unknown): number | null => (v == null ? null : Number(v));

/** Postgres numerics arrive as strings — normalize so callers can trust the types. */
export async function getUsage(): Promise<Usage> {
  const r = await api.get<Usage>('/pwa/api/usage');
  return {
    ...r,
    usage: {
      input_tokens: num(r.usage.input_tokens),
      output_tokens: num(r.usage.output_tokens),
      total_tokens: num(r.usage.total_tokens),
      calls: num(r.usage.calls),
      cost_usd: num(r.usage.cost_usd),
    },
    limits: {
      monthly_token_cap: numOrNull(r.limits.monthly_token_cap),
      monthly_call_cap: numOrNull(r.limits.monthly_call_cap),
      rate_limit_per_min: num(r.limits.rate_limit_per_min),
    },
    remaining: {
      tokens: numOrNull(r.remaining.tokens),
      calls: numOrNull(r.remaining.calls),
    },
  };
}

export async function getUsageDaily(days = 30): Promise<DailyPoint[]> {
  const r = await api.get<{ days: DailyPoint[] }>(`/pwa/api/usage/daily?days=${days}`);
  return r.days.map((d) => ({
    day: d.day,
    total_tokens: num(d.total_tokens),
    calls: num(d.calls),
    cost_usd: num(d.cost_usd),
  }));
}

export async function getUsageRecent(): Promise<RecentCall[]> {
  const r = await api.get<{ calls: RecentCall[] }>('/pwa/api/usage/recent');
  return r.calls.map((c) => ({
    ...c,
    input_tokens: num(c.input_tokens),
    output_tokens: num(c.output_tokens),
    total_tokens: num(c.total_tokens),
    cost_usd: num(c.cost_usd),
    provider_ms: numOrNull(c.provider_ms),
    client_ms: numOrNull(c.client_ms),
  }));
}

export async function getUsageByModel(): Promise<ModelRow[]> {
  const r = await api.get<{ models: ModelRow[] }>('/pwa/api/usage/by-model');
  return r.models.map((m) => ({
    ...m,
    calls: num(m.calls),
    total_tokens: num(m.total_tokens),
    cost_usd: num(m.cost_usd),
  }));
}
